import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail]   = useState(searchParams.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState("");

  const handleSubmit = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
      } else {
        setError(e.response?.data?.error || e.response?.data?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "var(--bg-primary)" }}>
      <style>{`
        @keyframes fpFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fp-card {
          background: var(--bg-card);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          padding: 40px 36px;
          width: 100%;
          max-width: 420px;
          animation: fpFadeIn 0.3s ease both;
        }
        .fp-title {
          font-family: var(--font-serif);
          font-size: 26px;
          font-weight: 400;
          color: var(--text-logo);
          margin: 0 0 6px;
        }
        .fp-sub {
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0 0 28px;
        }
        .fp-error {
          font-size: 12px;
          color: var(--danger-text);
          padding: 10px 12px;
          background: var(--danger-bg);
          border: 1px solid var(--danger-border);
          border-radius: var(--radius-sm);
          margin-top: 12px;
        }
        .fp-link {
          background: none;
          border: none;
          font-family: var(--font-sans);
          font-size: 12px;
          color: var(--accent);
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: opacity 0.15s;
        }
        .fp-link:hover { opacity: 0.8; }
        .fp-sent-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--accent-bg, rgba(16,185,129,0.12));
          border: 1px solid var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: var(--accent);
          font-size: 20px;
        }
      `}</style>

      <div className="fp-card">
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
          <span className="tc-dot" />
          <span style={{ fontFamily: "var(--font-serif)", fontSize: 18, color: "var(--text-logo)", letterSpacing: "0.01em" }}>
            TableCure
          </span>
        </div>

        {!sent ? (
          <>
            <h2 className="fp-title">Forgot password</h2>
            <p className="fp-sub">
              Enter your account email and we'll send you a one-time code to reset your password.
            </p>

            <div className="tc-field">
              <label className="tc-label">Email</label>
              <input
                className="tc-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                autoFocus
              />
            </div>

            {error && <div className="fp-error">{error}</div>}

            <button
              className="tc-btn tc-btn-primary"
              style={{ width: "100%", padding: "12px", fontSize: "12px", justifyContent: "center", marginTop: 20, marginBottom: 16 }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                  Sending…
                </span>
              ) : "Send reset code"}
            </button>

            <p style={{ textAlign: "center", fontSize: 12, color: "var(--text-muted)" }}>
              Remember your password?{" "}
              <button className="fp-link" onClick={() => navigate("/")}>Sign in</button>
            </p>
          </>
        ) : (
          <div style={{ textAlign: "center" }}>
            <div className="fp-sent-icon">✓</div>
            <h2 className="fp-title">Check your inbox</h2>
            <p className="fp-sub">
              If an account with{" "}
              <strong style={{ color: "var(--text-primary)" }}>{email}</strong>{" "}
              exists, we've sent a 6-digit reset code to it.
            </p>

            <button
              className="tc-btn tc-btn-primary"
              style={{ width: "100%", padding: "12px", fontSize: "12px", justifyContent: "center", marginBottom: 16 }}
              onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
            >
              Enter OTP
            </button>

            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Didn't get it?{" "}
              <button className="fp-link" onClick={() => setSent(false)}>Try a different email</button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
